/**
 * Every call this console makes to the API, in one file, so the REST surface it depends on is
 * readable in one screen and drift against apps/api/src/routes/*.ts is one diff.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { api } from "./api.js";
import { pushCommand } from "./push.js";
import type {
  Asset,
  BoardResponse,
  ChecklistTemplate,
  ConflictDetailResponse,
  ConflictListResponse,
  ConflictResolveResponse,
  Customer,
  CustomFieldDef,
  DashboardStats,
  Device,
  HistoryResponse,
  InvoiceDetailResponse,
  InvoiceListResponse,
  InvoiceStatus,
  IssueInvoiceResponse,
  JobDetailResponse,
  JobListResponse,
  JobType,
  Material,
  PaymentListResponse,
  PaymentMethod,
  PriceList,
  RecordPaymentResponse,
  Role,
  Site,
  TechnicianStatsResponse,
  TrialBalanceResponse,
  User,
} from "./types.js";

/** The dispatch board is the only screen where staleness is visible to the manager. */
export const BOARD_POLL_MS = 15_000;

// ── dashboard ────────────────────────────────────────────────────────────────

export const useStats = (days = 30): UseQueryResult<DashboardStats> =>
  useQuery({
    queryKey: ["stats", days],
    queryFn: () => api<DashboardStats>("/dashboard/stats", { query: { days } }),
    refetchInterval: 60_000,
  });

export const useTechnicianStats = (days = 30): UseQueryResult<TechnicianStatsResponse> =>
  useQuery({
    queryKey: ["tech-stats", days],
    queryFn: () => api<TechnicianStatsResponse>("/dashboard/technicians", { query: { days } }),
  });

// ── jobs ─────────────────────────────────────────────────────────────────────

export interface JobFilters {
  status?: string;
  technician_id?: string;
  customer_id?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export const useJobs = (filters: JobFilters): UseQueryResult<JobListResponse> =>
  useQuery({
    queryKey: ["jobs", filters],
    queryFn: () => api<JobListResponse>("/jobs", { query: { ...filters } }),
  });

export const useBoard = (date: string): UseQueryResult<BoardResponse> =>
  useQuery({
    queryKey: ["board", date],
    queryFn: () => api<BoardResponse>("/jobs/board", { query: { date } }),
    refetchInterval: BOARD_POLL_MS,
  });

export const useJob = (id: string | undefined): UseQueryResult<JobDetailResponse> =>
  useQuery({
    queryKey: ["job", id],
    queryFn: () => api<JobDetailResponse>(`/jobs/${id}`),
    enabled: Boolean(id),
  });

export const useJobHistory = (id: string | undefined): UseQueryResult<HistoryResponse> =>
  useQuery({
    queryKey: ["job-history", id],
    queryFn: () => api<HistoryResponse>(`/jobs/${id}/history`),
    enabled: Boolean(id),
  });

// ── job commands (via /sync/push — see lib/push.ts) ───────────────────────────

/** Invalidates everything a job write can be seen through. Cheap; the console is online. */
function useJobInvalidation() {
  const qc = useQueryClient();
  return (jobId?: string) => {
    void qc.invalidateQueries({ queryKey: ["board"] });
    void qc.invalidateQueries({ queryKey: ["jobs"] });
    void qc.invalidateQueries({ queryKey: ["stats"] });
    if (jobId) {
      void qc.invalidateQueries({ queryKey: ["job", jobId] });
      void qc.invalidateQueries({ queryKey: ["job-history", jobId] });
    }
  };
}

/** SYNC.md Rule 5: `job.assign` is LWW on server receipt. Two dispatchers, later one wins. */
export function useAssign() {
  const invalidate = useJobInvalidation();
  return useMutation({
    mutationFn: (v: { jobId: string; technicianId: string | null }) =>
      pushCommand("job.assign", v.jobId, { technician_id: v.technicianId }),
    onSuccess: (_r, v) => invalidate(v.jobId),
  });
}

/**
 * The office half of the state machine. Callers must offer only what
 * `allowedTransitions(status, "office")` returns — the applier will refuse anything else, and
 * a button that produces a guaranteed rejection is a lie about what the office can do.
 */
export function useSetStatus() {
  const invalidate = useJobInvalidation();
  return useMutation({
    mutationFn: (v: { jobId: string; to: string; reason?: string }) =>
      pushCommand("job.status.set", v.jobId, {
        to: v.to,
        reason: v.reason,
        at: new Date().toISOString(),
      }),
    onSuccess: (_r, v) => invalidate(v.jobId),
  });
}

/** Per-FIELD LWW: only the keys sent are touched. Never send a whole job. */
export function useSetFields() {
  const invalidate = useJobInvalidation();
  return useMutation({
    mutationFn: (v: { jobId: string; fields: Record<string, unknown> }) =>
      pushCommand("job.field.set", v.jobId, { fields: v.fields }),
    onSuccess: (_r, v) => invalidate(v.jobId),
  });
}

export function useCreateJob() {
  const invalidate = useJobInvalidation();
  return useMutation({
    mutationFn: (v: { jobId: string; payload: Record<string, unknown> }) =>
      pushCommand("job.create", v.jobId, v.payload),
    onSuccess: () => invalidate(),
  });
}

export function useAddNote() {
  const invalidate = useJobInvalidation();
  return useMutation({
    mutationFn: (v: { jobId: string; noteId: string; body: string }) =>
      pushCommand("job.note.add", v.jobId, { note_id: v.noteId, body: v.body }),
    onSuccess: (_r, v) => invalidate(v.jobId),
  });
}

// ── conflicts ────────────────────────────────────────────────────────────────

export const useConflicts = (includeResolved: boolean): UseQueryResult<ConflictListResponse> =>
  useQuery({
    queryKey: ["conflicts", includeResolved],
    queryFn: () =>
      api<ConflictListResponse>("/conflicts", {
        query: includeResolved ? { resolved: "true" } : {},
      }),
    refetchInterval: 60_000,
  });

export const useConflict = (id: string | undefined): UseQueryResult<ConflictDetailResponse> =>
  useQuery({
    queryKey: ["conflict", id],
    queryFn: () => api<ConflictDetailResponse>(`/conflicts/${id}`),
    enabled: Boolean(id),
  });

export const useConflictCount = (): UseQueryResult<{ unresolved: number }> =>
  useQuery({
    queryKey: ["conflict-count"],
    queryFn: () => api<{ unresolved: number }>("/conflicts/meta/count"),
    refetchInterval: 60_000,
  });

export function useResolveConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; resolution: "accepted" | "discarded"; note?: string }) =>
      api<ConflictResolveResponse>(`/conflicts/${v.id}/resolve`, {
        method: "POST",
        body: { resolution: v.resolution, note: v.note },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["conflicts"] });
      void qc.invalidateQueries({ queryKey: ["conflict"] });
      void qc.invalidateQueries({ queryKey: ["conflict-count"] });
      void qc.invalidateQueries({ queryKey: ["board"] });
      void qc.invalidateQueries({ queryKey: ["jobs"] });
      void qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

// ── customers / sites / assets ───────────────────────────────────────────────

export const useCustomers = (): UseQueryResult<{ customers: Customer[] }> =>
  useQuery({
    queryKey: ["customers"],
    queryFn: () => api<{ customers: Customer[] }>("/catalog/customers", { query: { limit: 500 } }),
  });

export const useCustomer = (
  id: string | undefined,
): UseQueryResult<{ customer: Customer; sites: Site[] }> =>
  useQuery({
    queryKey: ["customer", id],
    queryFn: () => api<{ customer: Customer; sites: Site[] }>(`/catalog/customers/${id}`),
    enabled: Boolean(id),
  });

export const useSites = (customerId?: string): UseQueryResult<{ sites: Site[] }> =>
  useQuery({
    queryKey: ["sites", customerId ?? "all"],
    queryFn: () => api<{ sites: Site[] }>("/catalog/sites", { query: { customer_id: customerId } }),
  });

export const useAssets = (siteId?: string): UseQueryResult<{ assets: Asset[] }> =>
  useQuery({
    queryKey: ["assets", siteId ?? "all"],
    queryFn: () => api<{ assets: Asset[] }>("/catalog/assets", { query: { site_id: siteId } }),
  });

function useCatalogMutation<V>(fn: (v: V) => Promise<unknown>, keys: string[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of keys) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
}

export const useCreateCustomer = () =>
  useCatalogMutation(
    (v: { name: string; phone: string; alt_phone?: string; notes?: string }) =>
      api<{ id: string }>("/catalog/customers", { method: "POST", body: v }),
    ["customers"],
  );

export const useCreateSite = () =>
  useCatalogMutation(
    (v: { customer_id: string; label: string; address?: string; lat?: number | null; lng?: number | null }) =>
      api<{ id: string }>("/catalog/sites", { method: "POST", body: v }),
    ["sites", "customer"],
  );

export const useCreateAsset = () =>
  useCatalogMutation(
    (v: {
      site_id: string;
      kind?: string;
      brand?: string;
      model?: string;
      serial?: string;
      capacity_btu?: number;
      installed_at?: string;
      notes?: string;
    }) => api<{ id: string }>("/catalog/assets", { method: "POST", body: v }),
    ["assets"],
  );

// ── catalogue / configurability ──────────────────────────────────────────────

export const useMaterials = (): UseQueryResult<{ materials: Material[] }> =>
  useQuery({
    queryKey: ["materials"],
    queryFn: () => api<{ materials: Material[] }>("/catalog/materials"),
  });

export const useCreateMaterial = () =>
  useCatalogMutation(
    (v: {
      sku?: string;
      name_ar: string;
      unit: string;
      default_cost_fils: number;
      default_price_fils: number;
      track_stock: boolean;
    }) => api<{ id: string }>("/catalog/materials", { method: "POST", body: v }),
    ["materials"],
  );

export const usePriceLists = (): UseQueryResult<{ price_lists: PriceList[] }> =>
  useQuery({
    queryKey: ["price-lists"],
    queryFn: () => api<{ price_lists: PriceList[] }>("/catalog/price-lists"),
  });

export const useCreatePriceList = () =>
  useCatalogMutation(
    (v: { name_ar: string; is_default: boolean; customer_id?: string | null }) =>
      api<{ id: string }>("/catalog/price-lists", { method: "POST", body: v }),
    ["price-lists"],
  );

export const useSetPrice = () =>
  useCatalogMutation(
    (v: { listId: string; material_id: string; unit_price_fils: number }) =>
      api<{ id: string }>(`/catalog/price-lists/${v.listId}/items`, {
        method: "POST",
        body: { material_id: v.material_id, unit_price_fils: v.unit_price_fils },
      }),
    ["price-lists"],
  );

export const useJobTypes = (): UseQueryResult<{ job_types: JobType[] }> =>
  useQuery({
    queryKey: ["job-types"],
    queryFn: () => api<{ job_types: JobType[] }>("/catalog/job-types"),
  });

export const useCreateJobType = () =>
  useCatalogMutation(
    (v: { name_ar: string; color?: string; default_duration_mins: number }) =>
      api<{ id: string }>("/catalog/job-types", { method: "POST", body: v }),
    ["job-types"],
  );

export const useCustomFields = (): UseQueryResult<{ custom_fields: CustomFieldDef[] }> =>
  useQuery({
    queryKey: ["custom-fields"],
    queryFn: () => api<{ custom_fields: CustomFieldDef[] }>("/catalog/custom-fields"),
  });

export const useCreateCustomField = () =>
  useCatalogMutation(
    (v: {
      entity: string;
      job_type_id?: string | null;
      key: string;
      label_ar: string;
      kind: string;
      options: string[];
      required: boolean;
      sort: number;
    }) => api<{ id: string }>("/catalog/custom-fields", { method: "POST", body: v }),
    ["custom-fields"],
  );

export const useChecklists = (): UseQueryResult<{ checklists: ChecklistTemplate[] }> =>
  useQuery({
    queryKey: ["checklists"],
    queryFn: () => api<{ checklists: ChecklistTemplate[] }>("/catalog/checklists"),
  });

export const useCreateChecklist = () =>
  useCatalogMutation(
    (v: {
      job_type_id: string;
      items: Array<{ label_ar: string; kind: string; required: boolean }>;
    }) => api<{ id: string }>("/catalog/checklists", { method: "POST", body: v }),
    ["checklists"],
  );

// ── users ────────────────────────────────────────────────────────────────────

export const useUsers = (): UseQueryResult<{ users: User[] }> =>
  useQuery({ queryKey: ["users"], queryFn: () => api<{ users: User[] }>("/users") });

export const useDevices = (userId: string | undefined): UseQueryResult<{ devices: Device[] }> =>
  useQuery({
    queryKey: ["devices", userId],
    queryFn: () => api<{ devices: Device[] }>(`/users/${userId}/devices`),
    enabled: Boolean(userId),
  });

export const useCreateUser = () =>
  useCatalogMutation(
    (v: { name: string; phone: string; email?: string; role: Role; pin?: string; password?: string }) =>
      api<{ id: string }>("/users", { method: "POST", body: v }),
    ["users"],
  );

export const useUpdateUser = () =>
  useCatalogMutation(
    (v: { id: string; name?: string; active?: boolean; pin?: string; password?: string }) => {
      const { id, ...patch } = v;
      return api<{ ok: true }>(`/users/${id}`, { method: "PATCH", body: patch });
    },
    ["users"],
  );

// ── money ────────────────────────────────────────────────────────────────────

export const useInvoices = (status?: InvoiceStatus): UseQueryResult<InvoiceListResponse> =>
  useQuery({
    queryKey: ["invoices", status ?? "all"],
    queryFn: () => api<InvoiceListResponse>("/money/invoices", { query: { status, limit: 500 } }),
  });

export const useInvoice = (id: string | undefined): UseQueryResult<InvoiceDetailResponse> =>
  useQuery({
    queryKey: ["invoice", id],
    queryFn: () => api<InvoiceDetailResponse>(`/money/invoices/${id}`),
    enabled: Boolean(id),
  });

export const usePayments = (): UseQueryResult<PaymentListResponse> =>
  useQuery({
    queryKey: ["payments"],
    queryFn: () => api<PaymentListResponse>("/money/payments", { query: { limit: 500 } }),
  });

function useMoneyMutation<V>(fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of ["invoices", "invoice", "payments", "job", "jobs", "board", "stats", "trial-balance"]) {
        void qc.invalidateQueries({ queryKey: [k] });
      }
    },
  });
}

/** The number comes back from the server. The console never proposes one. SYNC.md Rule 4. */
export const useIssueInvoice = () =>
  useMoneyMutation((v: { job_id: string; discount_fils: number }) =>
    api<IssueInvoiceResponse>("/money/invoices", { method: "POST", body: v }),
  );

export const useRecordPayment = () =>
  useMoneyMutation(
    (v: {
      invoice_id: string;
      amount_fils: number;
      method: PaymentMethod;
      received_at?: string;
      reference?: string;
    }) => api<RecordPaymentResponse>("/money/payments", { method: "POST", body: v }),
  );

export const useTrialBalance = (): UseQueryResult<TrialBalanceResponse> =>
  useQuery({
    queryKey: ["trial-balance"],
    queryFn: () => api<TrialBalanceResponse>("/money/ledger/trial-balance"),
  });

export const useSeedChart = () =>
  useMoneyMutation(() => api<{ ok: true; message_ar: string }>("/money/ledger/seed", { method: "POST" }));
